export const patientServicePageLimit = 1000;

export type DataSourceConfig<
  TResource = any,
  TParams extends DataSourceParams = DataSourceParams<any>,
> = {
  /** Unique identifier for this data source */
  key: string;
  /** Function to fetch the data for all patients */
  fetchData: (params: TParams) => Promise<TResource[]>;
  /** Function to map fetched data to a specific patient */
  mapToPatient: (params: {
    resources: TResource[];
    patientId: string;
  }) => TResource[];
  /** Optional function to sort/filter the mapped data */
  processData?: (resources: TResource[]) => TResource[];
};

export type DataSourceParams<
  T extends Record<string, any> = Record<string, any>,
> = {
  currentPatientIds: string;
  dataset: { codingToken: string };
} & T;

// Type that maps each data source key to its fetchData return type
export type DataSourceReturnTypes<T extends Record<string, DataSourceConfig>> =
  {
    [K in keyof T]: ReturnType<T[K]['fetchData']> extends Promise<infer T>
      ? T
      : never;
  };

// Function to fetch all data sources for a set of patients
export const fetchResourcesForPatients = async <
  T extends Record<string, DataSourceConfig>,
>(
  dataSources: T,
  params: DataSourceParams,
): Promise<DataSourceReturnTypes<T>> => {
  // If no patient IDs, return empty arrays for all data sources
  if (!params.currentPatientIds.length) {
    const emptyResults = {} as DataSourceReturnTypes<T>;
    Object.keys(dataSources).forEach(key => {
      (emptyResults as any)[key] = [];
    });
    return emptyResults;
  }

  const results = await Promise.all(
    Object.entries(dataSources).map(async ([key, dataSource]) => {
      try {
        const data = await dataSource.fetchData(params as any);
        return { key, data };
      } catch (error) {
        console.error(`Error fetching data source ${key}:`, error);
        return { key, data: null };
      }
    }),
  );

  const resultMap = {} as DataSourceReturnTypes<T>;
  results.forEach(({ key, data }) => {
    (resultMap as any)[key] = data;
  });

  return resultMap;
};

// Function to map all data sources to a specific patient
export const mapResourcesToPatient = <
  T extends Record<string, DataSourceConfig>,
>(
  dataSources: T,
  allData: DataSourceReturnTypes<T>,
  patientId: string,
): DataSourceReturnTypes<T> => {
  const patientData: DataSourceReturnTypes<T> = {} as DataSourceReturnTypes<T>;

  Object.entries(dataSources).forEach(([key, dataSource]) => {
    const resources = (allData as any)[key] || [];
    let mappedData = (dataSource as any).mapToPatient({ resources, patientId });

    if ((dataSource as any).processData) {
      mappedData = (dataSource as any).processData(mappedData);
    }

    // @ts-expect-error - force generic to return expected type
    patientData[key] = mappedData;
  });

  return patientData;
};
